// src/services/api.service.ts
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from 'axios'
import apiConfig from './api.config'
import tokenService from './token.service'
import { ApiErrorResponse } from '../types/api-error-response'

class ApiService {
  private readonly client: AxiosInstance
  private isRefreshing = false
  private refreshSubscribers: Array<(token: string) => void> = []

  constructor() {
    this.client = axios.create({
      baseURL: apiConfig.baseUrl,
      timeout: apiConfig.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  /**
   * Setup axios interceptors
   */
  private setupInterceptors(): void {
    // Request interceptor for adding the auth token
    this.client.interceptors.request.use(
      (config: any) => {
        const token = tokenService.getAccessToken()

        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }

        return config
      },
      (error: any) => Promise.reject(error)
    )

    // Response interceptor for handling token refresh
    this.client.interceptors.response.use(
      (response: any) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config

        // If error is not 401 or config is undefined or already retried, reject
        if (
          !error.response ||
          error.response.status !== 401 ||
          !originalRequest ||
          (originalRequest as any)._retry
        ) {
          return Promise.reject(error)
        }

        // Mark this request as retried
        ;(originalRequest as any)._retry = true

        // If not currently refreshing, attempt to refresh token
        if (!this.isRefreshing) {
          this.isRefreshing = true

          try {
            const refreshToken = tokenService.getRefreshToken()

            if (!refreshToken) {
              // No refresh token available, redirect to login
              this.handleAuthError()
              return Promise.reject(error)
            }

            const response = await this.refreshTokenRequest(refreshToken)
            const {
              accessToken,
              refreshToken: newRefreshToken,
              expiresIn,
            } = response.data

            tokenService.setTokens(accessToken, newRefreshToken, expiresIn)

            // Notify all subscribers about the new token
            this.onRefreshed(accessToken)

            // Update authorization header for the original request
            originalRequest.headers.Authorization = `Bearer ${accessToken}`

            return this.client(originalRequest)
          } catch (refreshError) {
            // Refresh token failed, redirect to login
            this.handleAuthError()
            return Promise.reject(refreshError)
          } finally {
            this.isRefreshing = false
          }
        }

        // If already refreshing, add request to queue
        return new Promise((resolve) => {
          this.subscribeToRefresh((token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            resolve(this.client(originalRequest))
          })
        })
      }
    )
  }

  /**
   * Handle authentication error (logout and redirect)
   */
  private handleAuthError(): void {
    tokenService.clearTokens()
    // In a real application, you might want to use a router to redirect to login
    window.location.href = '/signin'
  }

  /**
   * Make a request to refresh the token
   */
  private refreshTokenRequest(refreshToken: string): Promise<AxiosResponse> {
    return axios.post(
      `${apiConfig.baseUrl}${apiConfig.endpoints.auth.refresh}`,
      { refreshToken }
    )
  }

  /**
   * Subscribe to token refresh
   */
  private subscribeToRefresh(callback: (token: string) => void): void {
    this.refreshSubscribers.push(callback)
  }

  /**
   * Notify subscribers about the new token
   */
  private onRefreshed(token: string): void {
    this.refreshSubscribers.forEach((callback) => callback(token))
    this.refreshSubscribers = []
  }

  /**
   * Generic API request method
   */
  public async request<T>(config: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.client(config)
      return response.data
    } catch (error) {
      // Handle specific API errors here if needed
      throw error
    }
  }

  /**
   * GET request
   */
  public async get<T>(url: string, params?: any): Promise<T> {
    return this.request<T>({ method: 'GET', url, params })
  }

  /**
   * POST request
   */
  public async post<T>(url: string, data?: any): Promise<T> {
    return this.request<T>({ method: 'POST', url, data })
  }

  /**
   * PUT request
   */
  public async put<T>(url: string, data?: any): Promise<T> {
    return this.request<T>({ method: 'PUT', url, data })
  }

  /**
   * PATCH request
   */
  public async patch<T>(url: string, data?: any): Promise<T> {
    return this.request<T>({ method: 'PATCH', url, data })
  }

  /**
   * DELETE request
   */
  public async delete<T>(url: string, params?: any): Promise<T> {
    return this.request<T>({ method: 'DELETE', url, params })
  }
  public async fetchStream<T>(url: string, offer: RTCSessionDescriptionInit) {
    const token = tokenService.getAccessToken()

    return await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/sdp',
        Authorization: `Bearer ${token}`, // Add auth if needed
      },
      body: offer.sdp,
    })
  }
}

export const extractErrorMessage = (error: unknown): string => {
  // Default fallback message
  let errorMessage = 'An unexpected error occurred. Please try again.'

  if (error && typeof error === 'object') {
    const err = error as any

    // Handle Axios errors
    if (err.response?.data) {
      const data: ApiErrorResponse = err.response.data

      if (data.responseObject?.errorMessage) {
        errorMessage = data.responseObject.errorMessage
      } else if (data.message) {
        errorMessage = data.message
      } else if (data.error) {
        errorMessage = data.error
      }
    } else if (err.message) {
      errorMessage = err.message
    }
  }

  return errorMessage
}

export const apiService = new ApiService()
export default apiService
